// Pure geometry for the caret-anchored suggestion menu. No DOM access, no React, so it can be
// unit tested directly even though jsdom cannot lay out text (see QueryInput.tsx's caret
// measurement, which always reads 0 there).

import { MENU_VIEWPORT_PADDING_PX } from './floatingMenuGeometry';

const CARET_MENU_GAP_PX = 8;

export interface CaretMenuGeometryInput {
    inputRect: Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom'>;
    /** Caret distance from the start of the text box, already adjusted for scrollLeft. */
    caretOffsetPx: number;
    menuWidth: number;
    menuHeight: number;
    viewportWidth: number;
    viewportHeight: number;
}

export interface CaretMenuGeometry {
    top: number;
    left: number;
    /** What the menu may occupy once the viewport has had its say. */
    width: number;
    openUp: boolean;
}

export const computeCaretMenuGeometry = ({
    inputRect,
    caretOffsetPx,
    menuWidth,
    menuHeight,
    viewportWidth,
    viewportHeight,
}: CaretMenuGeometryInput): CaretMenuGeometry => {
    // Capped before `left` is derived: a menu wider than the viewport
    // otherwise pins to the left padding and still runs off the right edge.
    const width = Math.min(menuWidth, viewportWidth - MENU_VIEWPORT_PADDING_PX * 2);
    const maxLeft = viewportWidth - width - MENU_VIEWPORT_PADDING_PX;
    const left = Math.max(
        MENU_VIEWPORT_PADDING_PX,
        Math.min(inputRect.left + caretOffsetPx, maxLeft),
    );

    const spaceBelow = viewportHeight - inputRect.bottom;
    const openUp = menuHeight > 0 && spaceBelow < menuHeight + MENU_VIEWPORT_PADDING_PX;

    return {
        top: openUp
            ? inputRect.top - menuHeight - CARET_MENU_GAP_PX
            : inputRect.bottom + CARET_MENU_GAP_PX,
        left,
        width,
        openUp,
    };
};
